import {
  useGetPainSpikesByPatientIdQuery,
  useGetPainSpikesDailyCountByPatientIdQuery,
  useGetTerraPayloadsByPatientIdQuery,
} from '@api/patients.api';
import {
  Box,
  Divider,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Paper,
  Text,
  Title,
} from '@mantine/core';
import { LineChart } from '@mantine/charts';
import { useMemo, useState } from 'react';
import {
  dateToDateString,
  dateToHourString,
  formatSecondsToHoursMinutesSeconds,
} from '@utils/date';
import { IconHeart } from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';
import TerraDailyDataRecap from './TerraDailyDataRecap';

interface PatientGraphsProps {
  patientId: string;
}

export default function PatientGraphs({ patientId }: PatientGraphsProps) {
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());

  const {
    data: terraPayloads,
    isLoading: isLoadingTerraPayloads,
    isFetching: isFetchingTerraPayloads,
  } = useGetTerraPayloadsByPatientIdQuery({
    patientId,
    params: { day: selectedDay.toISOString() },
  });

  const dailyDataDomain = useMemo(() => {
    const min = new Date(selectedDay);
    min.setHours(0, 0, 0, 0);
    const max = new Date(selectedDay);
    max.setHours(23, 59, 59, 999);

    return [min, max];
  }, [selectedDay]);

  // const weeklyDataDomain = useMemo(() => {
  //   const today = new Date();
  //   const min = new Date(today);
  //   min.setDate(min.getDate() - 6);
  //   const max = new Date(today);

  //   return [min, max];
  // }, []);

  // const last7DaysData = useMemo(() => {
  //   return terraPayloads?.last30Days.data.filter(
  //     (d) =>
  //       d.timestamp >= weeklyDataDomain[0].getTime() &&
  //       d.timestamp <= weeklyDataDomain[1].getTime(),
  //   );
  // }, [terraPayloads, weeklyDataDomain]);

  const monthlyDataDomain = useMemo(() => {
    const today = new Date();
    const min = new Date(today);
    min.setDate(min.getDate() - 29);
    const max = new Date(today);

    return [min, max];
  }, []);

  const { data: painSpikes = [], isLoading: isLoadingPainSpikes } =
    useGetPainSpikesByPatientIdQuery(
      {
        patientId,
        params: {
          minDate: dailyDataDomain[0].toISOString(),
          maxDate: dailyDataDomain[1].toISOString(),
        },
      },
      {
        skip: !terraPayloads?.requestedDay.dailySamples,
      },
    );

  const {
    data: painSpikesDailyCount = [],
    isLoading: isLoadingPainSpikesDailyCount,
  } = useGetPainSpikesDailyCountByPatientIdQuery({
    patientId,
    params: {
      minDate: monthlyDataDomain[0].toISOString(),
      maxDate: monthlyDataDomain[1].toISOString(),
    },
  });

  // const painSpikesChartDataWeekly = useMemo(() => {
  //   const minT = weeklyDataDomain[0].getTime();
  //   const maxT = weeklyDataDomain[1].getTime();
  //   return painSpikesDailyCount.filter(
  //     (d) => d.timestamp >= minT && d.timestamp <= maxT,
  //   );
  // }, [painSpikesDailyCount, weeklyDataDomain]);

  return (
    <>
      <Group>
        <IconHeart />
        <Title order={3}>Dati Terra giornalieri</Title>
      </Group>

      <Box pos="relative" mt="lg">
        <LoadingOverlay
          visible={isLoadingPainSpikes || isLoadingTerraPayloads}
        />

        <Group align="center" gap="xl">
          <DatePickerInput
            label="Giorno selezionato"
            w="15rem"
            value={selectedDay}
            onChange={(value) => setSelectedDay(new Date(value ?? new Date()))}
            allowDeselect={false}
            maxDate={new Date()}
            disabled={isFetchingTerraPayloads}
          />
          {isFetchingTerraPayloads && (
            <Group pt="md">
              <Loader size="xs" />
              <Text>Aggiornamento dati</Text>
            </Group>
          )}
        </Group>

        <Paper withBorder p="md" mt="md" mb="xl">
          {terraPayloads?.requestedDay.data ? (
            <TerraDailyDataRecap
              data={terraPayloads.requestedDay.data}
              sleepData={terraPayloads.requestedDay.sleepData}
            />
          ) : (
            <Text fs="italic">
              Nessun dato disponibile per il giorno selezionato
            </Text>
          )}
        </Paper>

        <Grid mt="xl" gutter="xl">
          <>
            {terraPayloads?.requestedDay.dailySamples &&
              terraPayloads.requestedDay.dailySamples.heartRate.length > 1 && (
                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Frequenza cardiaca
                  </Title>
                  <LineChart
                    title="Frequenza cardiaca"
                    h={300}
                    data={terraPayloads.requestedDay.dailySamples.heartRate.map(
                      (sample) => ({
                        timestamp: sample.timestamp,
                        bpm: sample.bpm,
                      }),
                    )}
                    withDots={false}
                    dataKey="timestamp"
                    series={[{ name: 'bpm', color: 'brand.6' }]}
                    valueFormatter={(value) => value.toFixed(0)}
                    referenceLines={painSpikes.map((spike) => ({
                      x: new Date(spike.createdAt).getTime(),
                      label: 'PS',
                      color: 'red.6',
                    }))}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        dailyDataDomain[0].getTime(),
                        dailyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'daily',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>
              )}
            {terraPayloads?.requestedDay.dailySamples &&
              terraPayloads.requestedDay.dailySamples.stress.length > 1 && (
                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Stress
                  </Title>
                  <LineChart
                    title="Stress"
                    h={300}
                    data={terraPayloads.requestedDay.dailySamples.stress.map(
                      (sample) => ({
                        timestamp: sample.timestamp,
                        level: sample.level,
                      }),
                    )}
                    withDots={false}
                    dataKey="timestamp"
                    series={[{ name: 'level', color: 'brand.6' }]}
                    valueFormatter={(value) => value.toFixed(0)}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        dailyDataDomain[0].getTime(),
                        dailyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'daily',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>
              )}
            {terraPayloads?.requestedDay.dailySamples &&
              terraPayloads.requestedDay.dailySamples.oxygenSaturation.length >
                1 && (
                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Ossigenazione sangue
                  </Title>
                  <LineChart
                    title="Ossigenazione sangue"
                    h={300}
                    data={terraPayloads.requestedDay.dailySamples.oxygenSaturation.map(
                      (sample) => ({
                        timestamp: sample.timestamp,
                        percentage: sample.percentage,
                      }),
                    )}
                    withDots={false}
                    dataKey="timestamp"
                    series={[{ name: 'percentage', color: 'brand.6' }]}
                    valueFormatter={(value) => value.toFixed(0)}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        dailyDataDomain[0].getTime(),
                        dailyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'daily',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>
              )}

            <Grid.Col span={12}>
              <Divider />
            </Grid.Col>

            {terraPayloads?.requestedDay.sleepSamples &&
              terraPayloads.requestedDay.sleepSamples.sleepHypnogram.length >
                1 && (
                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Fasi del sonno
                  </Title>
                  <LineChart
                    title="Ipnogramma"
                    h={300}
                    data={terraPayloads.requestedDay.sleepSamples.sleepHypnogram.map(
                      (sample) => ({
                        timestamp: sample.timestamp,
                        level: sample.level,
                      }),
                    )}
                    withDots={false}
                    dataKey="timestamp"
                    series={[
                      {
                        name: 'level',
                        label: 'Fase del sonno',
                        color: 'indigo.6',
                      },
                    ]}
                    valueFormatter={(value) => {
                      const sleepStageMap: Record<number, string> = {
                        0: 'Sconosciuto',
                        1: 'Sveglio',
                        2: 'Sonno',
                        3: 'Fuori dal letto',
                        4: 'Leggero',
                        5: 'Profondo',
                        6: 'REM',
                      };
                      return (
                        sleepStageMap[Math.round(value)] ||
                        `Livello ${value.toFixed(0)}`
                      );
                    }}
                    referenceLines={painSpikes.map((spike) => ({
                      x: new Date(spike.createdAt).getTime(),
                      label: 'PS',
                      color: 'red.6',
                    }))}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        dailyDataDomain[0].getTime(),
                        dailyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    yAxisProps={{
                      domain: [0, 4],
                      ticks: [0, 1, 2, 3, 4, 5, 6],
                      tickFormatter: (value) => {
                        const sleepStageMap: Record<number, string> = {
                          0: 'Sconosciuto',
                          1: 'Sveglio',
                          2: 'Sonno',
                          3: 'Fuori dal letto',
                          4: 'Sonno leggero',
                          5: 'Sonno profondo',
                          6: 'REM',
                        };
                        return sleepStageMap[value] || '';
                      },
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'daily',
                      syncMethod: 'value',
                    }}
                    curveType="stepAfter"
                    strokeWidth={2}
                  />
                </Grid.Col>
              )}
            {terraPayloads?.requestedDay.sleepSamples &&
              terraPayloads.requestedDay.sleepSamples.oxygenSaturation.length >
                1 && (
                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Ossigenazione sangue (sonno)
                  </Title>
                  <LineChart
                    title="Ossigenazione sangue"
                    h={300}
                    data={terraPayloads.requestedDay.sleepSamples.oxygenSaturation.map(
                      (sample) => ({
                        timestamp: sample.timestamp,
                        percentage: sample.percentage,
                      }),
                    )}
                    withDots={false}
                    dataKey="timestamp"
                    series={[{ name: 'percentage', color: 'brand.6' }]}
                    valueFormatter={(value) => value.toFixed(0)}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        dailyDataDomain[0].getTime(),
                        dailyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToHourString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'daily',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>
              )}
          </>
        </Grid>
      </Box>

      {/* <Group mt="xl">
        <IconHeart />
        <Title order={3}>Dati Terra ultimi 7 giorni</Title>
      </Group>

      <Box pos="relative" mt="lg">
        <LoadingOverlay
          visible={
            isLoadingPainSpikes ||
            isLoadingTerraPayloads ||
            isLoadingPainSpikesDailyCount
          }
        />

        <Grid mt="xl" gutter="xl">
          {last7DaysData && last7DaysData.length > 0 && (
            <>
              <Grid.Col span={6}>
                <Title order={5} mb="sm">
                  Frequenza cardiaca
                </Title>
                <LineChart
                  title="Frequenza cardiaca"
                  h={300}
                  withLegend
                  data={last7DaysData.map((dailyData) => ({
                    timestamp: dailyData.timestamp,
                    Minima: dailyData.heartRateDaily.min_hr_bpm,
                    Media: dailyData.heartRateDaily.avg_hr_bpm,
                    Massima: dailyData.heartRateDaily.max_hr_bpm,
                    RMSSD: dailyData.heartRateDaily.avg_hrv_rmssd,
                    SDNN: dailyData.heartRateDaily.avg_hrv_sdnn,
                  }))}
                  dataKey="timestamp"
                  series={[
                    { name: 'Minima', color: 'blue.6' },
                    { name: 'Media', color: 'orange.6' },
                    { name: 'Massima', color: 'red.6' },
                    { name: 'RMSSD', color: 'red.6' },
                    { name: 'SDNN', color: 'yellow.6' },
                  ]}
                  valueFormatter={(value) => value.toFixed(0)}
                  xAxisProps={{
                    type: 'number',
                    domain: [
                      weeklyDataDomain[0].getTime(),
                      weeklyDataDomain[1].getTime(),
                    ],
                    tickFormatter: (value) => dateToDateString(new Date(value)),
                  }}
                  tooltipProps={{
                    labelFormatter: (value) =>
                      dateToDateString(new Date(value)),
                  }}
                  lineChartProps={{
                    syncId: 'weekly',
                    syncMethod: 'value',
                  }}
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <Title order={5} mb="sm">
                  Calorie
                </Title>
                <LineChart
                  title="Calorie"
                  h={300}
                  withLegend
                  data={last7DaysData.map((dailyData) => ({
                    timestamp: dailyData.timestamp,
                    Assunte: dailyData.caloriesDaily.net_intake_calories,
                  }))}
                  dataKey="timestamp"
                  series={[{ name: 'Assunte', color: 'brand.6' }]}
                  valueFormatter={(value) => value.toFixed(0)}
                  xAxisProps={{
                    type: 'number',
                    domain: [
                      weeklyDataDomain[0].getTime(),
                      weeklyDataDomain[1].getTime(),
                    ],
                    tickFormatter: (value) => dateToDateString(new Date(value)),
                  }}
                  tooltipProps={{
                    labelFormatter: (value) =>
                      dateToDateString(new Date(value)),
                  }}
                  lineChartProps={{
                    syncId: 'weekly',
                    syncMethod: 'value',
                  }}
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <Title order={5} mb="sm">
                  Stress
                </Title>
                <LineChart
                  title="Stress"
                  h={300}
                  withLegend
                  data={last7DaysData.map((dailyData) => ({
                    timestamp: dailyData.timestamp,
                    Stress: dailyData.stressDaily.avg_stress_level,
                  }))}
                  dataKey="timestamp"
                  series={[{ name: 'Stress', color: 'brand.6' }]}
                  valueFormatter={(value) => value.toFixed(0)}
                  xAxisProps={{
                    type: 'number',
                    domain: [
                      weeklyDataDomain[0].getTime(),
                      weeklyDataDomain[1].getTime(),
                    ],
                    tickFormatter: (value) => dateToDateString(new Date(value)),
                  }}
                  tooltipProps={{
                    labelFormatter: (value) =>
                      dateToDateString(new Date(value)),
                  }}
                  lineChartProps={{
                    syncId: 'weekly',
                    syncMethod: 'value',
                  }}
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <Title order={5} mb="sm">
                  Attività
                </Title>
                <LineChart
                  title="Attività"
                  h={300}
                  withLegend
                  data={last7DaysData.map((dailyData) => ({
                    timestamp: dailyData.timestamp,
                    Inattività:
                      dailyData.activeDurationsDaily.inactivity_seconds,
                    'Attività bassa':
                      dailyData.activeDurationsDaily.low_intensity_seconds,
                    'Attività moderata':
                      dailyData.activeDurationsDaily.moderate_intensity_seconds,
                    'Attività vigorosa':
                      dailyData.activeDurationsDaily.vigorous_intensity_seconds,
                    'Attività totale':
                      dailyData.activeDurationsDaily.activity_seconds,
                  }))}
                  dataKey="timestamp"
                  series={[
                    { name: 'Inattività', color: 'gray.6' },
                    { name: 'Attività bassa', color: 'blue.6' },
                    { name: 'Attività moderata', color: 'green.6' },
                    { name: 'Attività vigorosa', color: 'red.6' },
                    { name: 'Attività totale', color: 'yellow.6' },
                  ]}
                  valueFormatter={(value) =>
                    formatSecondsToHoursMinutesSeconds(value)
                  }
                  xAxisProps={{
                    type: 'number',
                    domain: [
                      weeklyDataDomain[0].getTime(),
                      weeklyDataDomain[1].getTime(),
                    ],
                    tickFormatter: (value) => dateToDateString(new Date(value)),
                  }}
                  tooltipProps={{
                    labelFormatter: (value) =>
                      dateToDateString(new Date(value)),
                  }}
                  lineChartProps={{
                    syncId: 'spikes',
                    syncMethod: 'value',
                  }}
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <Title order={5} mb="sm">
                  Distanza
                </Title>
                <LineChart
                  title="Distanza"
                  h={300}
                  withLegend
                  data={last7DaysData.map((dailyData) => ({
                    timestamp: dailyData.timestamp,
                    Passi: dailyData.distanceDaily.steps,
                    Distanza: dailyData.distanceDaily.distance_meters,
                  }))}
                  dataKey="timestamp"
                  series={[{ name: 'Distanza', color: 'brand.6' }]}
                  valueFormatter={(value) => value.toFixed(0)}
                  xAxisProps={{
                    type: 'number',
                    domain: [
                      weeklyDataDomain[0].getTime(),
                      weeklyDataDomain[1].getTime(),
                    ],
                    tickFormatter: (value) => dateToDateString(new Date(value)),
                  }}
                  tooltipProps={{
                    labelFormatter: (value) =>
                      dateToDateString(new Date(value)),
                  }}
                  lineChartProps={{
                    syncId: 'weekly',
                    syncMethod: 'value',
                  }}
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <Title order={5} mb="sm">
                  Passi
                </Title>
                <LineChart
                  title="Passi"
                  h={300}
                  withLegend
                  data={last7DaysData.map((dailyData) => ({
                    timestamp: dailyData.timestamp,
                    Passi: dailyData.distanceDaily.steps,
                  }))}
                  dataKey="timestamp"
                  series={[{ name: 'Passi', color: 'brand.6' }]}
                  valueFormatter={(value) => value.toFixed(0)}
                  xAxisProps={{
                    type: 'number',
                    domain: [
                      weeklyDataDomain[0].getTime(),
                      weeklyDataDomain[1].getTime(),
                    ],
                    tickFormatter: (value) => dateToDateString(new Date(value)),
                  }}
                  tooltipProps={{
                    labelFormatter: (value) =>
                      dateToDateString(new Date(value)),
                  }}
                  lineChartProps={{
                    syncId: 'weekly',
                    syncMethod: 'value',
                  }}
                />
              </Grid.Col>

              <Grid.Col span={6}>
                <Title order={5} mb="sm">
                  Pain Spikes
                </Title>
                <LineChart
                  title="Pain Spikes"
                  h={300}
                  withLegend
                  data={painSpikesChartDataWeekly.map((d) => ({
                    timestamp: d.timestamp,
                    Count: d.count,
                  }))}
                  dataKey="timestamp"
                  series={[
                    {
                      name: 'Count',
                      label: 'Pain Spikes segnalati',
                      color: 'red.6',
                    },
                  ]}
                  valueFormatter={(value) => value.toFixed(0)}
                  xAxisProps={{
                    type: 'number',
                    domain: [
                      weeklyDataDomain[0].getTime(),
                      weeklyDataDomain[1].getTime(),
                    ],
                    tickFormatter: (value) => dateToDateString(new Date(value)),
                  }}
                  tooltipProps={{
                    labelFormatter: (value) =>
                      dateToDateString(new Date(value)),
                  }}
                  lineChartProps={{
                    syncId: 'weekly',
                    syncMethod: 'value',
                  }}
                />
              </Grid.Col>
            </>
          )}
        </Grid>
      </Box> */}

      <Group mt="xl">
        <IconHeart />
        <Title order={3}>Dati Terra ultimi 30 giorni</Title>
      </Group>

      <Box pos="relative" mt="lg">
        <LoadingOverlay
          visible={
            isLoadingPainSpikes ||
            isLoadingTerraPayloads ||
            isLoadingPainSpikesDailyCount
          }
        />

        <Grid mt="xl" gutter="xl">
          {terraPayloads?.last30Days?.data &&
            terraPayloads.last30Days.data.length > 0 && (
              <>
                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Frequenza cardiaca
                  </Title>
                  <LineChart
                    title="Frequenza cardiaca"
                    h={300}
                    withLegend
                    data={terraPayloads.last30Days.data.map((dailyData) => ({
                      timestamp: dailyData.timestamp,
                      Minima: dailyData.heartRateDaily.min_hr_bpm,
                      Media: dailyData.heartRateDaily.avg_hr_bpm,
                      Massima: dailyData.heartRateDaily.max_hr_bpm,
                      RMSSD: dailyData.heartRateDaily.avg_hrv_rmssd,
                      SDNN: dailyData.heartRateDaily.avg_hrv_sdnn,
                    }))}
                    dataKey="timestamp"
                    series={[
                      { name: 'Minima', color: 'blue.6' },
                      { name: 'Media', color: 'orange.6' },
                      { name: 'Massima', color: 'red.6' },
                      { name: 'RMSSD', color: 'red.6' },
                      { name: 'SDNN', color: 'yellow.6' },
                    ]}
                    valueFormatter={(value) => value.toFixed(0)}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        monthlyDataDomain[0].getTime(),
                        monthlyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'monthly',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Calorie
                  </Title>
                  <LineChart
                    title="Calorie"
                    h={300}
                    withLegend
                    data={terraPayloads.last30Days.data.map((dailyData) => ({
                      timestamp: dailyData.timestamp,
                      Assunte: dailyData.caloriesDaily.net_intake_calories,
                    }))}
                    dataKey="timestamp"
                    series={[{ name: 'Assunte', color: 'brand.6' }]}
                    valueFormatter={(value) => value.toFixed(0)}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        monthlyDataDomain[0].getTime(),
                        monthlyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'monthly',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Stress
                  </Title>
                  <LineChart
                    title="Stress"
                    h={300}
                    withLegend
                    data={terraPayloads.last30Days.data.map((dailyData) => ({
                      timestamp: dailyData.timestamp,
                      Stress: dailyData.stressDaily.avg_stress_level,
                    }))}
                    dataKey="timestamp"
                    series={[{ name: 'Stress', color: 'brand.6' }]}
                    valueFormatter={(value) => value.toFixed(0)}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        monthlyDataDomain[0].getTime(),
                        monthlyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'monthly',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Attività
                  </Title>
                  <LineChart
                    title="Attività"
                    h={300}
                    withLegend
                    data={terraPayloads.last30Days.data.map((dailyData) => ({
                      timestamp: dailyData.timestamp,
                      Inattività:
                        dailyData.activeDurationsDaily.inactivity_seconds,
                      'Attività bassa':
                        dailyData.activeDurationsDaily.low_intensity_seconds,
                      'Attività moderata':
                        dailyData.activeDurationsDaily
                          .moderate_intensity_seconds,
                      'Attività vigorosa':
                        dailyData.activeDurationsDaily
                          .vigorous_intensity_seconds,
                      'Attività totale':
                        dailyData.activeDurationsDaily.activity_seconds,
                    }))}
                    dataKey="timestamp"
                    series={[
                      { name: 'Inattività', color: 'gray.6' },
                      { name: 'Attività bassa', color: 'blue.6' },
                      { name: 'Attività moderata', color: 'green.6' },
                      { name: 'Attività vigorosa', color: 'red.6' },
                      { name: 'Attività totale', color: 'yellow.6' },
                    ]}
                    valueFormatter={(value) =>
                      formatSecondsToHoursMinutesSeconds(value)
                    }
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        monthlyDataDomain[0].getTime(),
                        monthlyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'monthly',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Distanza
                  </Title>
                  <LineChart
                    title="Distanza"
                    h={300}
                    withLegend
                    data={terraPayloads.last30Days.data.map((dailyData) => ({
                      timestamp: dailyData.timestamp,
                      Passi: dailyData.distanceDaily.steps,
                      Distanza: dailyData.distanceDaily.distance_meters,
                    }))}
                    dataKey="timestamp"
                    series={[{ name: 'Distanza', color: 'brand.6' }]}
                    valueFormatter={(value) => value.toFixed(0)}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        monthlyDataDomain[0].getTime(),
                        monthlyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'monthly',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Passi
                  </Title>
                  <LineChart
                    title="Passi"
                    h={300}
                    withLegend
                    data={terraPayloads.last30Days.data.map((dailyData) => ({
                      timestamp: dailyData.timestamp,
                      Passi: dailyData.distanceDaily.steps,
                    }))}
                    dataKey="timestamp"
                    series={[{ name: 'Passi', color: 'brand.6' }]}
                    valueFormatter={(value) => value.toFixed(0)}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        monthlyDataDomain[0].getTime(),
                        monthlyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'monthly',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <Title order={5} mb="sm">
                    Pain Spikes
                  </Title>
                  <LineChart
                    title="Pain Spikes"
                    h={300}
                    withLegend
                    data={painSpikesDailyCount.map((d) => ({
                      timestamp: d.timestamp,
                      Count: d.count,
                    }))}
                    dataKey="timestamp"
                    series={[
                      {
                        name: 'Count',
                        label: 'Pain Spikes segnalati',
                        color: 'red.6',
                      },
                    ]}
                    valueFormatter={(value) => value.toFixed(0)}
                    xAxisProps={{
                      type: 'number',
                      domain: [
                        monthlyDataDomain[0].getTime(),
                        monthlyDataDomain[1].getTime(),
                      ],
                      tickFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    tooltipProps={{
                      labelFormatter: (value) =>
                        dateToDateString(new Date(value)),
                    }}
                    lineChartProps={{
                      syncId: 'monthly',
                      syncMethod: 'value',
                    }}
                  />
                </Grid.Col>
              </>
            )}
        </Grid>
      </Box>
    </>
  );
}
